// Expense-statement exports (Round 8). Both renderers print the
// StatementDoc strings verbatim (Round 6 export-regression pattern — the
// pinned-figures test covers buildStatementDoc, which is exactly what
// lands on the page). PDF via jsPDF, DOCX via the docx package, mirroring
// the existing export plumbing.

import { jsPDF } from "jspdf";
import {
  Document,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
} from "docx";
import type { StatementDoc } from "./expenseStatement";
import { slugify, triggerDownload } from "./exportShared";

export async function exportStatementPdf(doc: StatementDoc): Promise<void> {
  const pdf = new jsPDF({ unit: "mm", format: "a4" });
  const margin = 18;
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const usable = pageW - margin * 2;
  // Column layout: Date | Description | Amount | Source, plus a Notes
  // column when the doc carries one (any included row has a user note).
  const fractions =
    doc.headers.length === 5
      ? [0.12, 0.3, 0.15, 0.23, 0.2]
      : [0.13, 0.42, 0.15, 0.3];
  const colW = fractions.map((f) => usable * f);
  const colX = colW.map((_, i) =>
    colW.slice(0, i).reduce((s, w) => s + w, margin),
  );
  let y = margin;

  const ensureRoom = (needed: number) => {
    if (y + needed > pageH - margin) {
      pdf.addPage();
      y = margin;
    }
  };

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(15);
  pdf.text(doc.title, margin, y);
  y += 7;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.text(doc.dateLine, margin, y);
  y += 8;

  // Header row.
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9.5);
  doc.headers.forEach((h, i) => pdf.text(h, colX[i]!, y));
  y += 2;
  pdf.line(margin, y, pageW - margin, y);
  y += 5;

  pdf.setFont("helvetica", "normal");
  for (const row of doc.rows) {
    const cells = row.map((text, i) => pdf.splitTextToSize(text, colW[i]! - 3));
    const rowH = Math.max(...cells.map((c) => c.length)) * 4.4 + 2.5;
    ensureRoom(rowH);
    cells.forEach((lines, i) => pdf.text(lines, colX[i]!, y));
    y += rowH;
  }

  ensureRoom(16);
  pdf.line(margin, y, pageW - margin, y);
  y += 6;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.text(doc.totalLine, colX[2]!, y);
  y += 10;

  pdf.setFont("helvetica", "italic");
  pdf.setFontSize(8.5);
  const footerLines = pdf.splitTextToSize(doc.footer, usable);
  ensureRoom(footerLines.length * 4);
  pdf.text(footerLines, margin, y);

  triggerDownload(
    pdf.output("blob"),
    `${slugify(doc.title)}.pdf`,
  );
}

export async function exportStatementDocx(doc: StatementDoc): Promise<void> {
  const headerRow = new TableRow({
    children: doc.headers.map(
      (h) =>
        new TableCell({
          children: [new Paragraph({ children: [new TextRun({ text: h, bold: true })] })],
        }),
    ),
  });
  const bodyRows = doc.rows.map(
    (cells) =>
      new TableRow({
        children: cells.map(
          (text) => new TableCell({ children: [new Paragraph(text)] }),
        ),
      }),
  );

  const document = new Document({
    sections: [
      {
        children: [
          new Paragraph({
            children: [new TextRun({ text: doc.title, bold: true, size: 30 })],
          }),
          new Paragraph(doc.dateLine),
          new Paragraph(""),
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows: [headerRow, ...bodyRows],
          }),
          new Paragraph(""),
          new Paragraph({
            children: [new TextRun({ text: doc.totalLine, bold: true })],
          }),
          new Paragraph(""),
          new Paragraph({
            children: [new TextRun({ text: doc.footer, italics: true, size: 17 })],
          }),
        ],
      },
    ],
  });

  const blob = await Packer.toBlob(document);
  triggerDownload(blob, `${slugify(doc.title)}.docx`);
}
